/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { GanttRowData, GanttRowAxisProps, GanttTaskData } from '../gantt.types';
import { Text } from '#UNSAFE_Text';
import type { getRowDataLayout } from './dataLayoutUtils';

type RowDataLayout<
  K1,
  K2,
  D1 extends GanttRowData<K1, D1, D2>,
  D2 extends GanttTaskData<K2>
> = ReturnType<typeof getRowDataLayout<K1, K2, D1, D2>>;

type RowAxisLabelRenderContext<D1> = Parameters<
  NonNullable<GanttRowAxisProps<D1>['labelRenderer']>
>[0];

/**
 * Gets the default row axis label renderer.
 * @param param0
 * @returns The default renderer.
 */
const defaultRowAxisLabelRender = <
  K1,
  K2,
  D1 extends GanttRowData<K1, D1, D2>,
  D2 extends GanttTaskData<K2>
>({
  maxWidth,
  maxHeight,
  data: { label = '', labelStyle }
}: RowAxisLabelRenderContext<D1>) => {
  return (
    <div
      style={{
        width: maxWidth > -1 ? maxWidth : undefined,
        height: maxHeight,
        display: 'flex',
        alignItems: 'center',
        ...labelStyle
      }}>
      <Text size="md" variant={labelStyle?.color ? 'inherit' : 'primary'} truncation="ellipsis">
        {label}
      </Text>
    </div>
  );
};

/**
 * Gets the row label renderer context.
 * @param layoutObj The row layout object.
 * @param rowData The rows data.
 * @param maxWidth The max width of the row axis.
 * @returns The renderer context.
 */
const getRenderContext = <
  K1,
  K2,
  D1 extends GanttRowData<K1, D1, D2>,
  D2 extends GanttTaskData<K2>
>(
  layoutObj: RowDataLayout<K1, K2, D1, D2>['rowObjs'][0],
  rowData: RowDataLayout<K1, K2, D1, D2>['rowData'],
  maxWidth: number
): RowAxisLabelRenderContext<D1> => {
  return {
    maxWidth,
    maxHeight: layoutObj.height,
    data: rowData[layoutObj.index]
  };
};

export { defaultRowAxisLabelRender, getRenderContext };
